import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { User } from '../types/auth.types';

export class FirestoreService {
  // Cache simple pour éviter les requêtes répétées
  private static userCache: Map<string, { user: User; timestamp: number }> = new Map();
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  static async createUser(user: User): Promise<void> {
    try {
      await setDoc(doc(db, 'users', user.id), user);
    } catch (error) {
      console.error('Erreur lors de la création du profil:', error);
      throw error;
    }
  }

  static async getUserById(userId: string): Promise<User | null> {
    try {
      // Vérifier le cache d'abord
      const cached = this.userCache.get(userId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        console.log('FirestoreService.getUserById - Utilisation du cache pour', userId);
        return cached.user;
      }

      console.log('FirestoreService.getUserById - Récupération depuis Firestore pour', userId);
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const user = { id: userDoc.id, ...userDoc.data() } as User;
        // Mettre en cache
        this.userCache.set(userId, { user, timestamp: Date.now() });
        return user;
      }
      
      console.warn('FirestoreService.getUserById - Utilisateur non trouvé:', userId);
      return null;
    } catch (error) {
      console.error('FirestoreService.getUserById - Erreur lors de la récupération du profil:', error);
      throw error;
    }
  }

  // Méthode pour vider le cache (utile lors de la déconnexion)
  static clearCache(): void {
    this.userCache.clear();
    console.log('FirestoreService.clearCache - Cache vidé');
  }

  static async getUserByEmail(email: string): Promise<User | null> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return { id: userDoc.id, ...userDoc.data() } as User;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération du profil par email:', error);
      throw error;
    }
  }

  static async updateUserProfile(userId: string, data: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...data,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  }

  static async isFirstUser(): Promise<boolean> {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      return querySnapshot.empty;
    } catch (error) {
      console.error('Erreur lors de la vérification du premier utilisateur:', error);
      throw error;
    }
  }

  static async getAllUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw error;
    }
  }
}
